/**
 * Settings › Platform — which recorder belongs to which customer.
 *
 * PLAN-2026-09-15 C#4, the recorder half, unblocked by decision 7.
 *
 * ── Why binding is the thing on this screen ──────────────────────────────
 *
 * Migration 036 exists because of a measured leak: footage was reachable
 * across tenants through a legacy id, and the fix was to bind each recorder to
 * exactly one customer. Until now that binding could only be made by running
 * `npm run db:bind-recorder` on the box — so the control that stops one
 * customer seeing another's footage lived in a shell history, and nothing in
 * the product could show what it was set to.
 *
 * ⚠️ There is no "add a recorder" here, and its absence is the design. A
 * recorder's ADDRESS is `RECORDER_URL` in the environment, and `db/reconcile.ts`
 * argues at length that it must have no default and no second source: a wrong
 * address makes every pass unreachable, which writes nothing, which ages every
 * reading out, which is byte-for-byte indistinguishable from a healthy fleet.
 * A second place to set it would be a way for the product and the reconciler to
 * disagree about which box holds the footage. The predecessor does keep a
 * recorder inventory with names and URLs, and that is the shape to reach for on
 * the day there are two recorders rather than one.
 *
 * ── Unbinding says what it costs ─────────────────────────────────────────
 *
 * It is not a tidy-up. While a recorder is unbound the reconcile refuses to
 * act, the registration sweep refuses to act, and Pulled Footage says no
 * recorder is bound — so a card that fails during that window has nowhere to
 * record. The confirmation says so in those words.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, api } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Empty,
  Field,
  Loading,
  Pill,
  Section,
} from './components/index.ts';
import type { ColumnDef } from '@tanstack/react-table';
import type { PillState } from './components/Pill.tsx';

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

interface LastRun {
  at: string;
  outcome: string | null;
  registered: number | null;
  unregistered: number | null;
  notWriting: number | null;
}

interface RecorderRow {
  recorderKey: string;
  tenant: { id: string; slug: string; name: string };
  boundAt: string;
  boundBy: string | null;
}

/** One row of `/api/platform/runs`, which reads as the console role. */
interface ReconcileRun {
  recorder_key: string;
  finished_at: string | null;
  outcome: string;
  registered: number | null;
  unregistered: number | null;
  not_writing: number | null;
}

interface TenantRow {
  id: string;
  slug: string;
  name: string;
}

/**
 * How a reconcile outcome reads on screen.
 *
 * 🔴 `empty_backup_set` is a SUCCESS and must not be drawn as a fault. It means
 * no camera currently needs backing up, which on a healthy fleet is the normal
 * state — `db/reconcile.ts` exits zero on it for the same reason.
 */
const OUTCOME_STATE: Record<string, PillState> = {
  ok: 'ok',
  empty_backup_set: 'ok',
  no_protectable_cameras: 'warning',
  recorder_unreachable: 'fault',
};

export function PlatformRecorders(): React.JSX.Element {
  const [rows, setRows] = useState<RecorderRow[] | null>(null);
  const [recorderUrl, setRecorderUrl] = useState<string | null>(null);
  const [runs, setRuns] = useState<Map<string, LastRun>>(new Map());
  /** False when the API has no console role, so no pass can be read at all. */
  const [runsReadable, setRunsReadable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [binding, setBinding] = useState(false);
  const [unbinding, setUnbinding] = useState<RecorderRow | null>(null);

  const load = useCallback(async () => {
    try {
      /*
       * 🔴 Two routes, because the two facts live behind different database
       * roles. `recorder_bindings` is a tenant table under RLS read as
       * cosmon_api; `backup_reconcile_runs` is cross-tenant and only
       * cosmon_console may read it. Joining them here keeps each route on one
       * role — the alternative was an endpoint straddling two pools, and the
       * first version of it ran the cross-tenant query before anybody had been
       * authorised.
       */
      const [bindings, passes] = await Promise.all([
        api.get<{ recorders: RecorderRow[]; recorderUrl: string | null }>('/api/platform/recorders'),
        api.get<{ configured?: boolean; reconcile: { runs: ReconcileRun[] } }>('/api/platform/runs'),
      ]);

      setRows(bindings.recorders);
      setRecorderUrl(bindings.recorderUrl);
      setRunsReadable(passes.configured !== false);

      // Newest finished pass per recorder. The route returns them newest first.
      const newest = new Map<string, LastRun>();
      for (const run of passes.reconcile.runs) {
        if (run.finished_at === null || newest.has(run.recorder_key)) continue;
        newest.set(run.recorder_key, {
          at: run.finished_at,
          outcome: run.outcome,
          registered: run.registered,
          unregistered: run.unregistered,
          notWriting: run.not_writing,
        });
      }
      setRuns(newest);
      setError(null);
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo<ColumnDef<RecorderRow, unknown>[]>(
    () => [
      {
        id: 'key',
        header: 'Recorder',
        accessorKey: 'recorderKey',
        cell: ({ row }) => <span className="mono cell-strong">{row.original.recorderKey}</span>,
      },
      {
        id: 'tenant',
        header: 'Belongs to',
        accessorFn: (r) => r.tenant.name,
        cell: ({ row }) => (
          <div>
            <div>{row.original.tenant.name}</div>
            <div className="small muted mono">{row.original.tenant.slug}</div>
          </div>
        ),
      },
      {
        id: 'last',
        header: 'Last reconcile',
        accessorFn: (r) => runs.get(r.recorderKey)?.at ?? '',
        cell: ({ row }) => {
          const run = runs.get(row.original.recorderKey) ?? null;
          // 🔴 "Bound" says nothing about whether the binding works. A recorder
          // bound to the wrong tenant and a recorder nothing can reach look
          // identical on a screen that shows only the binding.
          if (run === null) {
            /*
             * 🔴 "Not configured" and "nothing has run" are different answers
             * and must not share a line. DATABASE_URL_CONSOLE unset means the
             * reader is missing, not that the reconcile is idle — saying "no
             * pass has finished yet" there would be the exact false quiet the
             * platform console was built to stop.
             */
            return (
              <span className="small muted">
                {runsReadable
                  ? 'no pass has finished yet'
                  : 'DATABASE_URL_CONSOLE is not set, so passes cannot be read'}
              </span>
            );
          }
          return (
            <div>
              <Pill state={OUTCOME_STATE[run.outcome ?? ''] ?? 'warning'}>{run.outcome ?? 'unknown'}</Pill>
              <div className="small muted">
                {new Date(run.at).toLocaleString()}
                {run.outcome === 'ok'
                  ? ` · ${run.registered ?? 0} registered, ${run.unregistered ?? 0} not, ${run.notWriting ?? 0} not writing`
                  : ''}
              </div>
            </div>
          );
        },
      },
      {
        id: 'bound',
        header: 'Bound',
        accessorKey: 'boundAt',
        cell: ({ row }) => (
          <span className="small muted">
            {new Date(row.original.boundAt).toLocaleDateString()}
            {row.original.boundBy === null ? '' : ` by ${row.original.boundBy}`}
          </span>
        ),
        size: 180,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button variant="danger" size="sm" onClick={() => setUnbinding(row.original)}>
            Unbind
          </Button>
        ),
        size: 90,
      },
    ],
    [runs, runsReadable],
  );

  return (
    <>
      <Section
        flush
        title="Recorders"
        summary={
          rows === null
            ? undefined
            : `${rows.length} bound. ` +
              (recorderUrl === null
                ? 'This API has no RECORDER_URL, so footage screens cannot reach one.'
                : `Reached at ${recorderUrl}, which is set in the environment and not here.`)
        }
        actions={
          <Button variant="primary" size="sm" onClick={() => setBinding(true)}>
            Bind a recorder
          </Button>
        }
      >
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {rows === null ? (
          <Loading rows={2} cols={4} label="Loading recorders" />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            getRowId={(r) => r.recorderKey}
            initialSort={[{ id: 'key', desc: false }]}
            caption="Recorders bound to a tenant"
            empty={
              <Empty
                title="No recorder is bound"
                body="Until one is, the reconcile and the registration sweep both refuse to act, and a camera whose card fails has nowhere to record."
              />
            }
          />
        )}
      </Section>
      {binding && <BindDrawer onClose={() => setBinding(false)} onBound={() => void load()} />}
      {unbinding !== null && (
        <UnbindDrawer
          recorder={unbinding}
          onClose={() => setUnbinding(null)}
          onUnbound={() => void load()}
        />
      )}
    </>
  );
}

function BindDrawer({ onClose, onBound }: { onClose: () => void; onBound: () => void }): React.JSX.Element {
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [recorderKey, setRecorderKey] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setTenants((await api.get<{ tenants: TenantRow[] }>('/api/platform/tenants')).tenants);
      } catch (err) {
        setError(failure(err));
      }
    })();
  }, []);

  return (
    <Drawer
      open
      onClose={onClose}
      title="Bind a recorder"
      subtitle="The key is the name the reconciler already stores on every row. It is not a new name to invent."
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        <Field
          id="bind-key"
          label="Recorder key"
          hint="RECORDER_KEY in the deployment's environment. Changing it later orphans every registration, so it is decided once."
        >
          <input
            id="bind-key"
            type="text"
            className="mono"
            value={recorderKey}
            onChange={(event) => setRecorderKey(event.target.value)}
          />
        </Field>
        <Field
          id="bind-tenant"
          label="Belongs to"
          hint="One recorder, one customer. Binding a key another tenant holds is refused rather than moved."
        >
          <select id="bind-tenant" value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
            <option value="">Choose a tenant</option>
            {(tenants ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        <Button
          variant="primary"
          busy={busy}
          disabled={recorderKey.trim() === '' || tenantId === ''}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await api.post('/api/platform/recorders', {
                recorderKey: recorderKey.trim(),
                tenantId,
                bound: true,
              });
              onBound();
              onClose();
            } catch (err) {
              setError(failure(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          Bind
        </Button>
      </div>
    </Drawer>
  );
}

function UnbindDrawer({
  recorder,
  onClose,
  onUnbound,
}: {
  recorder: RecorderRow;
  onClose: () => void;
  onUnbound: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Drawer open onClose={onClose} title={`Unbind ${recorder.recorderKey}?`}>
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {/*
          Said before the button, not after it. Unbinding reads like a tidy-up
          and is not one: it stops the two jobs that put footage somewhere safe.
        */}
        <Banner tone="warn">
          While {recorder.tenant.name} has no recorder bound, the reconcile and the registration
          sweep both refuse to act, and Pulled Footage says no recorder is bound. A camera whose SD
          card fails during that window has nowhere to record.
        </Banner>
        <p className="small muted">
          Nothing on the recorder changes. Its rows, its footage and its settings are untouched —
          this is only about which customer cosmon-next will answer for.
        </p>
        <Button
          variant="danger"
          busy={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await api.post('/api/platform/recorders', {
                recorderKey: recorder.recorderKey,
                tenantId: recorder.tenant.id,
                bound: false,
              });
              onUnbound();
              onClose();
            } catch (err) {
              setError(failure(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          Unbind
        </Button>
      </div>
    </Drawer>
  );
}
